/**
 * Shared SWG Player Cities types (P14.2) — the client contract.
 *
 * The client RENDERS replicated city/citizen/civic/election rows and CALLS the
 * reducers; every rank, radius, tax, treasury, civic-destruction, and election
 * decision is server-authoritative. Mirrors the server `cities` tables.
 */

import type { IdentityHex } from './identity.js';

/** Rank names, index 0 → rank 1 (Outpost). */
export const CITY_RANK_NAMES = ['outpost', 'village', 'township', 'city', 'metropolis'] as const;
export type CityRankName = (typeof CITY_RANK_NAMES)[number];

/** Adoptable specializations (rank ≥ 3). */
export const CITY_SPECIALIZATIONS = ['stronghold', 'research', 'manufacturing', 'entertainment', 'medical', 'trade'] as const;
export type CitySpecialization = (typeof CITY_SPECIALIZATIONS)[number];

/** Civic structure types placeable via the city-zoning path. */
export const CIVIC_TYPES = ['cantina', 'hospital', 'bank', 'garage', 'theater', 'cloning', 'shuttleport', 'civic_decoration'] as const;
export type CivicType = (typeof CIVIC_TYPES)[number];

/** Each tax rate is a percent capped at this. */
export const CITY_MAX_TAX_PCT = 20;

/** A city. Mirrors `city`. */
export interface City {
  cityId: bigint;
  cityHallStructureId: bigint;
  mayor: IdentityHex;
  name: string;
  centerX: number;
  centerZ: number;
  /** 1..5 = Outpost..Metropolis. */
  cityRank: number;
  radiusM: number;
  treasury: bigint;
  incomeTax: number;
  propertyTax: number;
  salesTax: number;
  specialization: string;
  /** provisional | active | invalid. */
  status: string;
}

/** A declared resident. Mirrors `citizen`. */
export interface Citizen {
  citizenId: bigint;
  cityId: bigint;
  identity: IdentityHex;
  residenceStructureId: bigint;
  isMilitia: boolean;
}

// ─── Reducer argument types ──────────────────────────────────────────────────

export interface FoundCityArgs {
  cityHallStructureId: bigint;
  name: string;
}
export interface SetCityTaxArgs {
  cityId: bigint;
  income: number;
  property: number;
  sales: number;
}
export interface VoteMayorArgs {
  electionId: bigint;
  candidate: IdentityHex;
}
